import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * The kit menu (UI r2 deliverable 11; DECISIONS 88 item 2, 179): a surface
 * the kit owns, in place of Base UI's `Menu`, whose portal focus guards
 * (`aria-hidden` spans that take focus) fail axe's serious rule
 * `aria-hidden-focus` while a menu is open. Built as 1.F-1's block menu is
 * (`packages/editor/src/chrome/block-menu/menu-list.tsx`).
 *
 * - `MenuPopup` is the menu alone, for any trigger (`Menu`, r3's `CellMenu`):
 *   a `menu` named by `label`, portaled into the `PortalContainer` (else in
 *   place), fixed below its anchor or flipped above it when it does not fit;
 *   a `menu` layer, so Esc closes it alone; focus goes to the first enabled
 *   item and back to the anchor on close; past 12 rows it scrolls.
 * - Keys: Up and Down (wrapping, skipping disabled items), Home, End, Enter
 *   or Space (selects once), a printable key (type-ahead); Tab and a press
 *   outside close it.
 * - Classes are P0-9's (`cx-menu`, `cx-menu__item`, `__item--danger`,
 *   `__label`, `__hint`, `__separator`); the active item carries
 *   `data-highlighted`.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../icon/icon.js';
import { cx, useHasLayerProvider, useLayer, usePortalContainer, useReturnFocus } from './infra.js';
const GAP = 6;
const EDGE = 8;
const isHeading = (e) => typeof e === 'object' && 'heading' in e;
const isItem = (e) => typeof e === 'object' && !('heading' in e);
/** The entries as rows: items, separators and the named groups headings open. */
export function menuBlocks(items) {
    const blocks = [];
    let group = null;
    items.forEach((entry, i) => {
        if (entry === 'separator') {
            group = null;
            blocks.push({ kind: 'separator', key: i });
        }
        else if (isHeading(entry)) {
            group = { kind: 'group', key: i, heading: entry.heading, items: [] };
            blocks.push(group);
        }
        else if (group)
            group.items.push(i);
        else
            blocks.push({ kind: 'item', index: i });
    });
    return blocks;
}
/** The indexes of the enabled items, in order. */
export function enabledItems(items) {
    return items.flatMap((e, i) => (isItem(e) && !e.disabled ? [i] : []));
}
/** The next enabled item from `active` by `step`, wrapping. */
export function stepItem(items, active, step) {
    const list = enabledItems(items);
    if (list.length === 0)
        return -1;
    const at = list.indexOf(active);
    if (at < 0)
        return (step === 1 ? list[0] : list[list.length - 1]);
    return list[(at + step + list.length) % list.length];
}
/** The next enabled item after `active` whose label starts with `key` (type-ahead). */
export function typeAhead(items, active, key) {
    const list = enabledItems(items);
    const from = list.indexOf(active);
    const order = [...list.slice(from + 1), ...list.slice(0, from + 1)];
    const needle = key.toLowerCase();
    const hit = order.find((i) => items[i].label.toLowerCase().startsWith(needle));
    return hit ?? active;
}
/**
 * Where a menu of `size` goes beside `anchor` (viewport boxes): below, or
 * above when it does not fit below and fits better above; aligned to the
 * anchor's start, center or end and kept `EDGE` px inside the viewport.
 */
export function placeMenu(anchor, size, viewport, align) {
    const below = viewport.height - anchor.bottom - GAP - EDGE;
    const above = anchor.top - GAP - EDGE;
    const side = size.height > below && above > below ? 'above' : 'below';
    const top = side === 'below' ? anchor.bottom + GAP : anchor.top - GAP - size.height;
    const ideal = align === 'start'
        ? anchor.left
        : align === 'end'
            ? anchor.right - size.width
            : (anchor.left + anchor.right - size.width) / 2;
    const left = Math.max(EDGE, Math.min(ideal, viewport.width - EDGE - size.width));
    return { left, top: Math.max(EDGE, top), side };
}
export function MenuPopup({ open, onClose, anchor, label, items, align = 'end', testId, }) {
    const container = usePortalContainer();
    const hasProvider = useHasLayerProvider();
    const ref = useRef(null);
    const ids = useId();
    const [active, setActive] = useState(-1);
    const [place, setPlace] = useState(null);
    const closeRef = useRef(onClose);
    closeRef.current = onClose;
    const closeEsc = useCallback(() => closeRef.current('esc'), []);
    useLayer(open, closeEsc, 'menu');
    useReturnFocus(open, anchor);
    // Opening starts at the first enabled item; a re-render that passes new
    // items keeps the active one while it is still an enabled item.
    const wasOpen = useRef(false);
    useLayoutEffect(() => {
        const enabled = enabledItems(items);
        if (open && !wasOpen.current)
            setActive(enabled[0] ?? -1);
        else if (open)
            setActive((a) => (enabled.includes(a) ? a : (enabled[0] ?? -1)));
        else
            setPlace(null);
        wasOpen.current = open;
    }, [open, items]);
    // Focus follows the active item.
    useLayoutEffect(() => {
        if (!open)
            return;
        const el = ref.current?.querySelector(`[data-index="${active}"]`);
        (el ?? ref.current)?.focus();
    }, [open, active]);
    // Placement: on open and whenever the page scrolls or resizes under it.
    useLayoutEffect(() => {
        if (!open)
            return;
        const update = () => {
            const a = anchor.current;
            const menu = ref.current;
            if (!a || !menu)
                return;
            const next = placeMenu(a.getBoundingClientRect(), { width: menu.offsetWidth, height: menu.offsetHeight }, { width: window.innerWidth, height: window.innerHeight }, align);
            setPlace((prev) => prev && prev.left === next.left && prev.top === next.top && prev.side === next.side
                ? prev
                : next);
        };
        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [open, anchor, align]);
    // A press outside the menu and its anchor closes it.
    useEffect(() => {
        if (!open)
            return;
        const onPress = (event) => {
            const target = event.target;
            if (!target || ref.current?.contains(target) || anchor.current?.contains(target))
                return;
            closeRef.current('outside');
        };
        document.addEventListener('pointerdown', onPress, true);
        return () => document.removeEventListener('pointerdown', onPress, true);
    }, [open, anchor]);
    if (!open)
        return null;
    const select = (index) => {
        const item = items[index];
        if (!item || !isItem(item) || item.disabled)
            return;
        item.onSelect?.();
        closeRef.current('select');
    };
    const onKeyDown = (event) => {
        const key = event.key;
        if (key === 'ArrowDown' || key === 'ArrowUp')
            setActive(stepItem(items, active, key === 'ArrowDown' ? 1 : -1));
        else if (key === 'Home' || key === 'End') {
            const list = enabledItems(items);
            setActive((key === 'Home' ? list[0] : list[list.length - 1]) ?? -1);
        }
        else if (key === 'Enter' || key === ' ')
            select(active);
        else if (key === 'Tab') {
            // Focus goes back to the anchor; the Tab then moves on from there.
            closeRef.current('tab');
            return;
        }
        else if (key === 'Escape' && !hasProvider)
            closeRef.current('esc');
        else if (key.length === 1 &&
            /\S/u.test(key) &&
            !event.ctrlKey &&
            !event.metaKey &&
            !event.altKey)
            setActive(typeAhead(items, active, key));
        else
            return;
        event.preventDefault();
        event.stopPropagation();
    };
    const renderItem = (index) => {
        const item = items[index];
        const reasonId = item.disabled && item.disabledReason ? `${ids}-r${index}` : undefined;
        const descId = item.description ? `${ids}-d${index}` : undefined;
        const describedBy = [descId, reasonId].filter(Boolean).join(' ') || undefined;
        // Named by its label alone; the description and the reason describe it.
        const labelId = `${ids}-l${index}`;
        return (_jsxs("div", { "data-index": index, role: item.checked === undefined ? 'menuitem' : 'menuitemradio', "aria-checked": item.checked, "aria-disabled": item.disabled || undefined, "aria-labelledby": labelId, "aria-describedby": describedBy, tabIndex: index === active ? 0 : -1, "data-highlighted": index === active ? '' : undefined, "data-testid": item.testId, className: cx('cx-menu__item', item.danger && 'cx-menu__item--danger', item.disabled && 'cx-menu__item--disabled', (item.description || reasonId) && 'cx-menu__item--two-line'), onPointerEnter: () => {
                if (!item.disabled)
                    setActive(index);
            }, onClick: () => select(index), children: [item.icon && _jsx(Icon, { name: item.icon }), _jsxs("span", { className: "cx-menu__text", children: [_jsx("span", { id: labelId, className: "cx-menu__label", children: item.label }), item.description && (_jsx("span", { id: descId, className: "cx-menu__description", children: item.description })), reasonId && (_jsx("span", { id: reasonId, className: "cx-menu__reason", children: item.disabledReason }))] }), item.hint && _jsx("span", { className: "cx-menu__hint", children: item.hint }), item.checked && _jsx(Icon, { name: "check", className: "cx-menu__check" })] }, index));
    };
    const popup = (_jsx("div", { ref: ref, role: "menu", "aria-label": label, "aria-orientation": "vertical", tabIndex: -1, "data-testid": testId, "data-side": place?.side, className: "cx-menu cx-menu--popup", style: place ? { left: place.left, top: place.top } : { left: 0, top: 0, visibility: 'hidden' }, onKeyDown: onKeyDown, onMouseDown: (e) => e.preventDefault(), children: menuBlocks(items).map((block) => {
            if (block.kind === 'separator')
                // biome-ignore lint/a11y/useSemanticElements: a menu's separator, as ARIA's menu pattern has it
                return _jsx("div", { role: "separator", className: "cx-menu__separator" }, `s${block.key}`);
            if (block.kind === 'item')
                return renderItem(block.index);
            const headingId = `${ids}-h${block.key}`;
            return (_jsxs("div", { role: "group", "aria-labelledby": headingId, children: [_jsx("div", { id: headingId, role: "presentation", className: "cx-menu__heading", children: block.heading }), block.items.map(renderItem)] }, `g${block.key}`));
        }) }));
    return container ? createPortal(popup, container) : popup;
}
/** A trigger button and its `MenuPopup`. */
export function Menu({ label, items, icon = 'more', triggerLabel, align = 'end', testId, }) {
    const [open, setOpen] = useState(false);
    const anchor = useRef(null);
    return (_jsxs(_Fragment, { children: [_jsxs("button", { ref: anchor, type: "button", className: cx(triggerLabel
                    ? 'cx-button cx-button--secondary cx-button--sm'
                    : 'cx-icon-button cx-button--ghost'), "aria-label": triggerLabel ? undefined : label, "aria-haspopup": "menu", "aria-expanded": open, "data-popup-open": open ? '' : undefined, "data-testid": testId, onClick: () => setOpen((o) => !o), children: [_jsx(Icon, { name: icon, weight: open ? 'fill' : 'regular' }), triggerLabel && _jsx("span", { className: "cx-button__label", children: triggerLabel })] }), _jsx(MenuPopup, { open: open, onClose: () => setOpen(false), anchor: anchor, label: label, items: items, align: align })] }));
}
//# sourceMappingURL=menu.js.map